import { Suspense, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconFileVisualizationOutline } from '@iwoca/icons';
import { Button, Modal, Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { useGetTopupPendingBannerData } from './useGetTopupPendingBannerData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { NEW_IwLinkButton } from '../../../../iwRouter/NEW_IwLinkButton';
import { useGetDocumentFeedback } from '../../../myOffers/pendingView/applicationPendingView/PendingViewActionsDE/useGetDocumentFeedback';
import HelpModal from '../../../navigation/helpModal/HelpModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';

export const TopupPendingBanner = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    return (
        <>
            <div className="col-span-5 grid grid-cols-7 gap-m rounded-s border border-secondary-90 bg-primary-97 p-l max-sm:col-span-full">
                <div className="col-span-1 m-auto max-sm:col-span-full">
                    <IconFileVisualizationOutline
                        fontSize={48}
                        className="text-secondary-40"
                    />
                </div>
                <ErrorBoundary
                    fallback={() => (
                        <DefaultBanner
                            onOpenModal={() => setIsModalOpen(true)}
                        />
                    )}
                >
                    <Suspense fallback={<MainContentPending />}>
                        {getCountry() === 'DE' ? (
                            <DEBanner
                                onOpenModal={() => setIsModalOpen(true)}
                            />
                        ) : (
                            <DefaultBanner
                                onOpenModal={() => setIsModalOpen(true)}
                            />
                        )}
                    </Suspense>
                </ErrorBoundary>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <HelpModal />
            </Modal>
        </>
    );
};

const DEBanner = ({ onOpenModal }: { onOpenModal: () => void }) => {
    const { hasDocumentFeedback } = useGetDocumentFeedback();

    if (hasDocumentFeedback) {
        return <DocumentFeedbackBanner />;
    }

    return <DefaultBanner onOpenModal={onOpenModal} />;
};

const DocumentFeedbackBanner = () => {
    const { isRepaymentReductionApplication } = useGetTopupPendingBannerData();

    return (
        <>
            <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                <Text className="text-xl font-bold">
                    <FormattedMessage
                        id="_.dashboard.outstanding.doc_feedback_title"
                        defaultMessage="Almost there"
                    />
                </Text>
                <Text className="text-l">
                    {isRepaymentReductionApplication ? (
                        <FormattedMessage
                            id="_.dashboard.outstanding.doc_feedback_repayment_reduction_description"
                            defaultMessage="To progress your repayment reduction application, we need some additional information from you. Please visit the Documents page to review and action the feedback."
                        />
                    ) : (
                        <FormattedMessage
                            id="_.dashboard.outstanding.doc_feedback_topup_description"
                            defaultMessage="To progress your top-up application, we need some additional information from you. Please visit the Documents page to review and action the feedback."
                        />
                    )}
                </Text>
            </div>
            <div className="col-span-full max-sm:col-span-full max-sm:text-center min-m:col-start-2">
                <NEW_IwLinkButton
                    className="w-full min-sm:max-w-[300px]"
                    to={{ pathname: '/trading/' }}
                    onClick={() =>
                        tracking.ampli.dashboardActionClicked({
                            name: 'Review document feedback'
                        })
                    }
                >
                    <FormattedMessage
                        id="_.dashboard.outstanding.doc_feedback_cta"
                        defaultMessage="Review documents"
                    />
                </NEW_IwLinkButton>
            </div>
        </>
    );
};

const DefaultBanner = ({ onOpenModal }: { onOpenModal: () => void }) => {
    const { topUpAmount, isRepaymentReductionApplication } =
        useGetTopupPendingBannerData();

    return (
        <>
            <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                <Text className="text-xl font-bold">
                    {isRepaymentReductionApplication ? (
                        <FormattedMessage
                            id="_.dashboard.outstanding.repayment_reduction_pending_title"
                            defaultMessage="Repayment reduction application complete"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.rbl_dashboard.outstanding.topupPendingTitle"
                            defaultMessage="Top up requested"
                        />
                    )}
                </Text>
                <Text className="text-l">
                    {isRepaymentReductionApplication ? (
                        <FormattedMessage
                            id="_.dashboard.outstanding.repayment_reduction_pending_description"
                            defaultMessage="Thanks for your application to reduce your monthly repayments."
                        />
                    ) : (
                        <FormattedMessage
                            id="_.rbl_dashboard.outstanding.topupPendingDescription"
                            defaultMessage="Thanks for your application for a top up to a credit limit of {amount}"
                            values={{
                                amount: (
                                    <MoneyWithDecimals value={topUpAmount!} />
                                )
                            }}
                        />
                    )}
                </Text>
            </div>
            <div className="col-span-full max-sm:col-span-full max-sm:text-center min-m:col-start-2">
                <Button
                    onClick={() => {
                        tracking.ampli.topupChangeRequestCtaClicked();
                        onOpenModal();
                    }}
                >
                    <FormattedMessage
                        id="_.rbl_dashboard.outstanding.changeTopupRequest"
                        defaultMessage="Change request"
                    />
                </Button>
            </div>
        </>
    );
};
